import { Button } from 'antd';
import { useCallback, useState } from 'react';
import { UI_TEXT } from '../constants/index';
import { UseFormTrigger, UseFormGetValues } from 'react-hook-form';
import type { LoginFormData } from '../schemas';
import { API_CONFIG } from '@/constants';

interface SSOLoginProps {
  trigger: UseFormTrigger<LoginFormData>;
  getValues: UseFormGetValues<LoginFormData>;
  disabled?: boolean;
}

/**
 * SSO Login Component
 * Handles Single Sign-On authentication via OneLogin
 *
 * @remarks
 * - Uses shared company code field from parent form
 * - Validates company code before redirect
 * - Redirects to backend /api/v1/auth/sso/login
 * - Backend handles all SAML communication with OneLogin
 *
 * Flow:
 * 1. User enters company code in form
 * 2. User clicks SSO button → Validate company code (Zod)
 * 3. Redirect to backend: GET /api/v1/auth/sso/login?companyCode=XXX
 * 4. Backend:
 *    - Validate company (CHK001 if missing/invalid)
 *    - Generate SAML AuthnRequest
 *    - Redirect to OneLogin login page
 * 5. User logs in on OneLogin
 * 6. OneLogin POSTs SAMLResponse to backend: POST /api/v1/auth/sso/callback
 * 7. Backend:
 *    - Validate SAML (SSO001-SSO007 errors)
 *    - Set HttpOnly cookies (accessToken, refreshToken)
 *    - Redirect to frontend based on success/error
 * 8. Frontend handles redirect in /sso-callback or /auth with error params
 */
const SSOLogin: React.FC<SSOLoginProps> = ({
  trigger,
  getValues,
  disabled = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handle SSO button click
   * Validates company code and redirects to backend
   */
  const handleSSOLogin = useCallback(async () => {
    setIsLoading(true);

    // Step 1: Validate company code field (frontend validation)
    const isValid = await trigger('companyCode');
    if (!isValid) {
      setIsLoading(false);
      return;
    }

    // Step 2: Get company code value
    const companyCode = getValues('companyCode');

    // Step 3: Build SSO login URL with companyCode query parameter
    const ssoLoginUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.SSO_LOGIN_URL}?companyCode=${encodeURIComponent(companyCode)}`;

    // Step 4: Redirect to backend SSO endpoint
    window.location.href = ssoLoginUrl;
  }, [trigger, getValues]);

  return (
    <div className="mt-6">
      {/* Divider with text */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300 dark:border-gray-600" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-gray-50 text-gray-500">
            {UI_TEXT.SSO.DIVIDER}
          </span>
        </div>
      </div>

      {/* SSO Login Button - Uses company code from shared form */}
      <div className="mt-6">
        <Button
          type="default"
          onClick={handleSSOLogin}
          loading={isLoading}
          disabled={disabled || isLoading}
          className="w-full h-10 flex items-center justify-center border-gray-300 hover:border-blue-500 hover:text-blue-500 transition-colors"
        >
          {!isLoading && (
            <svg
              className="w-5 h-5 mr-2"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          )}
          {UI_TEXT.SSO.BUTTON_TEXT}
        </Button>
      </div>
    </div>
  );
};

export default SSOLogin;
